import { Injectable } from '@angular/core';

import { FsPresenceDevice, SnFloor } from '@lib/api/entities';

export interface FloorAlertState {
  floorId: string;
  deviceCount: number;
  criticalAlertCount: number;
  warningAlertCount: number;
}

/**
 * Service for aggregating device alert states by floor.
 * Pure transformer - does not fetch data, only processes it.
 *
 * Dependencies: @lib/api/entities only
 */
@Injectable()
export class FloorAlertStateService {
  private readonly SKYHOOK_PROVIDER = 'skyhook' as const;

  /**
   * Aggregates device alerts by floor ID.
   * Call this method whenever device data changes.
   */
  public aggregateFloorAlerts(
    devices: FsPresenceDevice[],
    floors: SnFloor[],
  ): Map<string, FloorAlertState> {
    const stateMap = new Map<string, FloorAlertState>();

    floors.forEach((floor) => {
      stateMap.set(floor.id, {
        floorId: floor.id,
        deviceCount: 0,
        criticalAlertCount: 0,
        warningAlertCount: 0,
      });
    });

    devices.forEach((device) => {
      const floorId = this._extractFloorId(device);

      if (!floorId || !stateMap.has(floorId)) {
        return;
      }

      const state = stateMap.get(floorId);
      if (!state) {
        return;
      }
      state.deviceCount++;

      if (device.properties?.rto?.panic) {
        state.criticalAlertCount++;
      } else if (device.properties?.rto?.watchMe) {
        state.warningAlertCount++;
      }
    });

    return stateMap;
  }

  /**
   * Extracts floor ID from device if provider is skyhook.
   */
  private _extractFloorId(device: FsPresenceDevice): string | null {
    const floor = device.properties?.floor;

    if (!floor?.id || floor.provider !== this.SKYHOOK_PROVIDER) {
      return null;
    }

    return floor.id;
  }
}
